// src/pages/DoctorDashboard.jsx
import { useState, useEffect } from "react";
import { collection, getDocs, query, where, doc, updateDoc, onSnapshot, orderBy, limit } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import ChatRoom from "../components/ChatRoom";
import {
  Users, MessageCircle, Clock, CheckCircle,
  XCircle, BarChart3, UserCheck, Inbox, UsersRound
} from "lucide-react";

function toArabicNum(n) {
  return n
}

const WA_ICON = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

export default function DoctorDashboard() {
  const { profile } = useAuth();
  const { lang, t } = useLanguage();
  const [tab, setTab] = useState("stats");

  return (
    <div className="page-content fade-in">
      <div className="container">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.5rem", flexWrap: "wrap", gap: "1rem" }}>
          <div>
            <h2>{t("doctorDashboard")}</h2>
            <p className="text-muted text-sm" style={{ margin: 0 }}>
              {lang === "ar" 
                ? `مرحباً، ${profile?.role === 'admin' ? '' : 'د. '}${profile?.name || ""}` 
                : `Welcome, ${profile?.role === 'admin' ? '' : 'Dr. '}${profile?.name || ""}`}
            </p>
          </div>
          <div className={`badge ${profile?.role === 'admin' ? 'badge-blue' : 'badge-green'}`}>
            {profile?.role === 'admin' 
              ? (lang === "ar" ? "مدير" : "Admin") 
              : (lang === "ar" ? "طبيب" : "Doctor")}
          </div>
        </div>

        {/* Tab Nav */}
        <div style={{
          display: "flex", gap: "0.4rem", background: "#3b5882",
          borderRadius: "var(--radius-md)", padding: "0.3rem", marginBottom: "2rem",
          flexWrap: "wrap"
        }}>
          {[
            { key: "stats",    icon: <BarChart3 size={16} />,  label: t("stats") },
            { key: "patients", icon: <UserCheck size={16} />,  label: t("patients") },
            { key: "inbox",    icon: <Inbox size={16} />,      label: t("messages") },
            { key: "group",    icon: <UsersRound size={16} />, label: t("groupChat") }
          ].map(item => (
            <button
              key={item.key}
              onClick={() => setTab(item.key)}
              className={`btn btn-sm flex items-center gap-2 ${tab === item.key ? "btn-primary" : "btn-ghost"}`}
              style={{ flex: "1 0 auto" }}
            >
              {item.icon} {item.label}
            </button>
          ))}
        </div>

        {tab === "stats"    && <StatsTab lang={lang} t={t} />}
        {tab === "patients" && <PatientsTab lang={lang} t={t} />}
        {tab === "inbox"    && <InboxTab lang={lang} t={t} />}
        {tab === "group"    && <GroupChatTab lang={lang} t={t} />}
      </div>
    </div>
  );
}

/* ── Stats ── */
function StatsTab({ lang, t }) {
  const [stats, setStats] = useState({ total: 0, paid: 0, free: 0, pending: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const allSnap = await getDocs(collection(db, "users"));
        const users = allSnap.docs.map(d => d.data()).filter(u => u.role === "parent");
        setStats({
          total:   users.length,
          paid:    users.filter(u => u.subscription === "paid" && u.status === "active").length,
          free:    users.filter(u => u.subscription !== "paid").length,
          pending: users.filter(u => u.status === "pending").length
        });
      } catch(e) { console.error(e); }
      setLoading(false);
    }
    load();
  }, []);

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  const statCards = [
    { label: t("totalUsers"),       value: stats.total,   icon: <Users size={22} />,        color: "var(--color-forest)" },
    { label: t("paidUsers"),        value: stats.paid,    icon: <CheckCircle size={22} />,  color: "var(--color-sky-dk)" },
    { label: t("freeUsers"),        value: stats.free,    icon: <MessageCircle size={22} />, color: "var(--color-earth)" },
    { label: t("pendingApprovals"), value: stats.pending, icon: <Clock size={22} />,         color: "var(--color-warning)" }
  ];

  return (
    <div className="grid-4" style={{ gap: "1rem" }}>
      {statCards.map((s, i) => (
        <div key={i} className="stat-card">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.75rem" }}>
            <span style={{ color: s.color }}>{s.icon}</span>
          </div>
          <div className="stat-value" style={{ color: s.color }}>
            {lang === "ar" ? toArabicNum(s.value) : s.value}
          </div>
          <div className="stat-label">{s.label}</div>
        </div>
      ))}
    </div>
  );
}

/* ── Patients List ── */
function PatientsTab({ lang, t }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState({});
  const [filter, setFilter] = useState("pending");

  useEffect(() => {
    const q = query(collection(db, "users"), where("role", "==", "parent"));
    const unsub = onSnapshot(q, snap => {
      setUsers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, []);

  async function handleApprove(uid) {
  setApproving(p => ({ ...p, [uid]: "approving" }));
  const now = new Date();
  const endDate = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
  await updateDoc(doc(db, "users", uid), {
    status: "active",
    subscription: "paid",
    subscriptionStartDate: now,
    subscriptionEndDate: endDate
  });
  setApproving(p => ({ ...p, [uid]: null }));
}

  async function handleReject(uid) {
    setApproving(p => ({ ...p, [uid]: "rejecting" }));
    await updateDoc(doc(db, "users", uid), { status: "rejected", subscription: "free" });
    setApproving(p => ({ ...p, [uid]: null }));
  }

  const filtered = users.filter(u => {
    if (filter === "pending") return u.status === "pending";
    if (filter === "active")  return u.status === "active" && u.subscription === "paid";
    return true;
  });

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  return (
    <div>
      <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1.25rem", flexWrap: "wrap" }}>
        {[
          { key: "pending", label: lang === "ar" ? "بانتظار الموافقة" : "Pending" },
          { key: "active",  label: lang === "ar" ? "نشطون" : "Active" },
          { key: "all",     label: lang === "ar" ? "الكل" : "All" }
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`btn btn-sm ${filter === f.key ? "btn-primary" : "btn-outline"}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center" style={{ padding: "3rem", color: "var(--color-gray-300)" }}>
          {lang === "ar" ? "لا يوجد مرضى في هذه الفئة" : "No patients in this category"}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
          {filtered.map(u => (
            <PatientCard
              key={u.id}
              user={u}
              lang={lang}
              t={t}
              onApprove={() => handleApprove(u.id)}
              onReject={() => handleReject(u.id)}
              approving={approving[u.id]}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PatientCard({ user, lang, t, onApprove, onReject, approving }) {
  const [expanded, setExpanded] = useState(false);

  const phone = user.phone;
  const waNumber = phone?.startsWith("0") ? "213" + phone.slice(1) : phone;

  return (
    <div className="card card-sm">
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap" }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.4rem", flexWrap: "wrap" }}>
            <span style={{ fontWeight: 700 }}>{user.name}</span>
            <span className={`badge ${
              user.subscription === "paid" && user.status === "active" ? "badge-green" :
              user.status === "pending" ? "badge-warn" : "badge-gray"
            }`}>
              {user.status === "pending" ? (lang === "ar" ? "بانتظار الموافقة" : "Pending") :
               user.status === "active"  ? (lang === "ar" ? "نشط" : "Active") :
               (lang === "ar" ? "مجاني" : "Free")}
            </span>
            {user.isADHD && <span className="badge badge-blue">ADHD</span>}
            {/* WhatsApp في بطاقة المريض */}
            {phone && (
              <a
                href={`https://wa.me/${waNumber}`}
                target="_blank"
                rel="noreferrer"
                style={{
                  display: "flex", alignItems: "center", justifyContent: "center",
                  width: 26, height: 26,
                  background: "#25D366",
                  borderRadius: "50%",
                  color: "#fff",
                  flexShrink: 0
                }}
                title={`واتساب: ${phone}`}
              >
                {WA_ICON}
              </a>
            )}
          </div>
          <div style={{ fontSize: "0.82rem", color: "#000000" }}>
            {user.email}
            {user.childAge && ` — ${lang === "ar" ? "الطفل:" : "Child:"} ${user.childName || ""} ${user.childAge} ${lang === "ar" ? "سنوات" : "yrs"}`}
          </div>
        </div>

        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
          {user.status === "pending" && (
            <>
              <button className="btn btn-sm btn-primary" onClick={onApprove} disabled={!!approving}>
                {approving === "approving"
                  ? <div className="spinner" style={{ width: 14, height: 14 }} />
                  : <><CheckCircle size={14} /> {t("approve")}</>}
              </button>
              <button
                className="btn btn-sm btn-outline"
                style={{ borderColor: "var(--color-error)", color: "var(--color-error)" }}
                onClick={onReject}
                disabled={!!approving}
              >
                <XCircle size={14} /> {t("reject")}
              </button>
            </>
          )}
          <button
            onClick={() => setExpanded(s => !s)}
            style={{
              padding: "0.4rem 1rem",
              borderRadius: "var(--radius-md)",
              border: `1.5px solid ${expanded ? "#3b5882" : "#3b5882"}`,
              background: expanded ? "#3b5882" : "#3b5882",
              color: expanded ? "#fff" : "#fff",
              fontSize: "0.82rem",
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "inherit",
              transition: "all 0.2s"
            }}
          >
            {expanded ? (lang === "ar" ? "إخفاء ▲" : "Hide ▲") : (lang === "ar" ? "عرض الملف ▼" : "View Profile ▼")}
          </button>
        </div>
      </div>

      {expanded && user.adhdProfile && (
        <div style={{ marginTop: "1rem", padding: "1rem", background: "var(--color-gray-50)", borderRadius: "var(--radius-md)", fontSize: "0.875rem" }}>
          <div style={{ fontWeight: 700, marginBottom: "0.5rem" }}>{lang === "ar" ? "ملف ADHD" : "ADHD Profile"}</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem" }}>
            <span>{lang === "ar" ? "الدرجة:" : "Score:"} <strong>{user.adhdProfile.percentage}%</strong></span>
            <span>{lang === "ar" ? "المستوى:" : "Level:"} <strong>{user.adhdProfile.riskLevel}</strong></span>
            {user.adhdProfile.symptomsHistory && (
              <span>{lang === "ar" ? "السجل:" : "History:"} {user.adhdProfile.symptomsHistory}</span>
            )}
          </div>
        </div>
      )}

      {expanded && user.receiptUrl && (
        <div style={{ marginTop: "0.75rem" }}>
          <a href={user.receiptUrl} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm">
            {lang === "ar" ? "عرض الإيصال" : "View Receipt"}
          </a>
        </div>
      )}
    </div>
  );
}

/* ── Inbox ── */
function InboxTab({ lang }) {
  const [chats, setChats]   = useState([]);
  const [users, setUsers]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [inboxTab, setInboxTab] = useState("all");

  useEffect(() => {
    // ── تحميل المحادثات ──
    const q = query(collection(db, "chats"), orderBy("lastMessageAt", "desc"), limit(50));
    const unsub = onSnapshot(q, snap => {
      setChats(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });

    // ── تحميل كل المستخدمين (للحصول على الاسم ورقم الهاتف) ──
    getDocs(collection(db, "users"))
      .then(snap => {
        setUsers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      })
      .catch(e => console.error("Users fetch error:", e));

    return unsub;
  }, []);

  const filtered = chats.filter(c => {
    if (c.id === "group_doctors") return false;
    if (inboxTab === "all")  return true;
    if (inboxTab === "paid") return c.id.startsWith("paid_") || c.type === "paid";
    if (inboxTab === "free") return c.id.startsWith("free_") || c.type === "free";
    return false;
  });

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  return (
    <div style={{ display: "grid", gridTemplateColumns: "300px 1fr", gap: "1.25rem", minHeight: 500 }}>

      {/* ── قائمة المحادثات ── */}
      <div>
        <div style={{ display: "flex", gap: "0.4rem", marginBottom: "1rem" }}>
          {["all", "paid", "free"].map(type => (
            <button
              key={type}
              onClick={() => setInboxTab(type)}
              className={`btn btn-sm flex-1 ${inboxTab === type ? "btn-primary" : "btn-outline"}`}
            >
              {type === "paid" ? (lang === "ar" ? "مدفوع" : "Paid")
               : type === "free" ? (lang === "ar" ? "مجاني" : "Free")
               : (lang === "ar" ? "الكل" : "All")}
            </button>
          ))}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", maxHeight: 550, overflowY: "auto" }}>
          {filtered.length === 0 && (
            <p className="text-muted text-sm text-center" style={{ padding: "2rem" }}>
              {lang === "ar" ? "لا توجد محادثات" : "No chats"}
            </p>
          )}

          {filtered.map(chat => {
            // استخراج uid من chatId
            const uid = chat.id
              .replace("paid_", "")
              .replace("free_", "")
              .split("_")[0];

            const patient  = users.find(x => x.id === uid);
            const phone    = patient?.phone;
            const waNumber = phone?.startsWith("0") ? "213" + phone.slice(1) : phone;

            return (
              <div
                key={chat.id}
                onClick={() => setSelected(chat.id)}
                style={{
                  padding: "0.85rem 1rem",
                  background: selected === chat.id ? "var(--color-forest-lt)" : "var(--color-white)",
                  border: `1.5px solid ${selected === chat.id ? "var(--color-forest)" : "var(--color-gray-200)"}`,
                  borderRadius: "var(--radius-md)",
                  transition: "all 0.15s",
                  cursor: "pointer"
                }}
              >
                {/* اسم المريض + زر واتساب */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.3rem" }}>
                  <span style={{ fontWeight: 600, fontSize: "0.875rem" }}>
                    {patient?.name || uid}
                  </span>
                  {phone && (
                    <a
                      href={`https://wa.me/${waNumber}`}
                      target="_blank"
                      rel="noreferrer"
                      onClick={e => e.stopPropagation()}
                      title={`واتساب: ${phone}`}
                      style={{
                        display: "flex", alignItems: "center", justifyContent: "center",
                        width: 28, height: 28,
                        background: "#25D366",
                        borderRadius: "50%",
                        color: "#fff",
                        flexShrink: 0,
                        textDecoration: "none"
                      }}
                    >
                      {WA_ICON}
                    </a>
                  )}
                </div>

                {/* Badge نوع المحادثة */}
                <div style={{ marginBottom: "0.25rem" }}>
                  <span
                    className={`badge ${chat.id.startsWith("paid_") ? "badge-green" : "badge-blue"}`}
                    style={{ fontSize: "0.68rem" }}
                  >
                    {chat.id.startsWith("paid_")
                      ? (lang === "ar" ? "مدفوع" : "Paid")
                      : (lang === "ar" ? "مجاني" : "Free")}
                  </span>
                </div>

                {/* آخر رسالة */}
                <div style={{ fontSize: "0.78rem", color: "var(--color-gray-400)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {chat.lastMessage || (lang === "ar" ? "لا توجد رسائل" : "No messages")}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── منطقة الشات ── */}
      <div>
        {selected ? (
          <ChatRoom chatId={selected} title="" />
        ) : (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "var(--color-gray-300)", fontSize: "0.875rem" }}>
            {lang === "ar" ? "اختر محادثة من القائمة" : "Select a chat from the list"}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Group Chat ── */
function GroupChatTab({ lang, t }) {
  return (
    <div>
      <div style={{ marginBottom: "1rem" }}>
        <h4>{lang === "ar" ? "مجموعة الأطباء" : "Doctors Group Chat"}</h4>
        <p className="text-muted text-sm" style={{ margin: 0 }}>
          {lang === "ar" ? "تنسيق داخلي بين الأطباء" : "Internal coordination between doctors"}
        </p>
      </div>
      <ChatRoom chatId="group_doctors" title="" />
    </div>
  );
}
